import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import {
  search_value,
  Filter,
  UserPlus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  User,
  Search,
  RotateCwIcon,
} from "lucide-react";
import Pagination from "../../components/Pagination";
import { useUsers } from "../../hooks/useUsers";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment/moment";
import UserForm from "./UserForm";
import { toast } from "react-toastify";
import { useResolvedPath } from "react-router-dom";
import Skeleton from "../../components/ui/Skeleton";
import DeleteModal from "../../components/ui/DeleteModal";
import ActionButton from "./ActionButton";

function UserManagement() {
  const { logout } = useAuth();
  const [usersList, setUsersList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [statusFilter, setStatusFilter] = useState({
    user_type: "user",
    filter: "",
  });
  const [permission, setPermission] = useState({});
  const [updateMode, setUpdateMode] = useState(false);
  const queryClient = useQueryClient();
  const [addressList, setAddressList] = useState([]);
  const path = useResolvedPath();
  const [deleteModal, setDeleteModal] = useState(false);
  const [pageData, setPageData] = useState({
    currentPage: 1,
    totalPages: Math.ceil(usersList.length / 10),
    showing: 10,
    total: 0,
  });
  const [filterObject, setFilterObject] = useState({});

  // New state for sorting
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  const {
    fetchUsers,
    createUser,
    deleteUser,
    updateUser,
    address,
    toggleUserStatus,
  } = useUsers({
    filters: { ...statusFilter, search_value: searchQuery },
    pagination: { page: pageData.currentPage, limit: 10 },
    sort: { field: sortField, direction: sortDirection },
  });

  const usersQuery = useQuery({
    queryKey: [
      "users",
      pageData.currentPage,
      statusFilter,
      searchQuery,
      sortField,
      sortDirection,
    ],
    queryFn: fetchUsers,
    keepPreviousData: true,
  });

  const addressQuery = useQuery({
    queryKey: ["address", statusFilter],
    queryFn: () => address({ type: "country" }),
    keepPreviousData: true,
  });

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : "asc"
      );
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    if (!addressQuery.isLoading) {
      setAddressList(addressQuery?.data?.data || []);
    }
  }, [addressQuery.isLoading, addressQuery?.data?.data]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPageData((prev)=>({
      ...prev,
      currentPage: 1,
    }))
  };

  const openModal = (user, mode) => {
    setSelectedUser(user);
    setModalMode(mode);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const handlePageChange = (page) => {
    setPageData((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  useEffect(() => {
    if (usersQuery.data && !usersQuery.isLoading) {
      const response = usersQuery.data;
      setUsersList(response?.data?.users || []);
      setPageData(() => ({
        currentPage: response?.data?.pagination?.currentPage || 1,
        totalPages: response?.data?.pagination?.lastPage || 1,
        total: response?.data?.pagination?.total || 0,
        showing: response?.data?.pagination?.showing || 10,
      }));
    }
  }, [
    usersQuery.data,
    pageData.showing,
    pageData.currentPage,
    usersQuery.isLoading,
  ]);

  const toggleStatus = (user) => {
    mutation.toggleStatus.mutate(user._id, {
      onSuccess: (data) => {
        toast.success("Status updated successfully");
      },
      onError: (error) => toast.error("Failed to update status"),
    });
  };

  const handleUpdate = (user) => {
    openModal(user, "edit");
    setUpdateMode(true);
  };

  const handleDelete = {
    onDelete: (user) =>
      mutation.deleteUser.mutate(user._id, {
        onSuccess: async (data) => {
          toast.success(data.message || "User Deleted");
          setDeleteModal(false);
        },
      }),
    onClose: () => setDeleteModal(false),
    onOpen: (user) => {
      setDeleteModal(true);
      setSelectedUser(user);
    },
  };

  const handleResetPassword = (userId) => {
    mutation.resetPassword.mutate(userId, {
      onSuccess: (data) => {
        toast.success(data.message || "Password reset successfully");
      },
      onError: (error) => {
        toast.error(error.message || "Failed to reset password");
      },
    });
  };

  const mutation = {
    createUser: useMutation({
      mutationFn: updateMode
        ? async (body) => await updateUser(body)
        : async (body) => await createUser(body),
    }),
    deleteUser: useMutation({
      mutationFn: async (id) => await deleteUser(id),
    }),
    toggleStatus: useMutation({
      mutationFn: async (id) => await toggleUserStatus(id),
    }),
    resetPassword: useMutation({
      mutationFn: async (id) => {
        const response = await fetch(`/api/users/${id}/reset-password`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to reset password");
        }
        return response.json();
      },
    }),
  };

  useEffect(() => {
    handleReset();
    if (path.pathname && path.pathname === "/admin-users") {
      setStatusFilter((prev) => ({
        ...prev,
        user_type: "admin",
      }));
    } else {
      setStatusFilter((prev) => ({
        ...prev,
        user_type: "user",
      }));
    }
  }, [path.pathname]);

  const handleSubmit = (body) => {
    mutation.createUser.mutate(body, {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(["user"]);
        toast.success(
          data?.data?.message ||
            `User ${updateMode ? "Updated" : "Created"} successfully`
        );
        setShowModal(false);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });
  };

  const USER_FILTER_FIELD = [
    // {
    //   value: "subscription_status",
    //   label: "Subscription Status",
    // },
    {
      value: "date_joined",
      label: "Date Joined",
    },
  ];

  const handleReset = () => {
    setStatusFilter((prev) => ({
      ...prev,
      filter: "",
    }));
    setFilterObject({ value: "", field: "" });
  };
  return (
    <div>
      <main>
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold">{path.pathname === "/admin-users" ? "Admin" : "User"} Management</h1>
          {permission.add && (
            <button
              onClick={() => openModal({}, "create")}
              className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Add New User
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border rounded-md w-10/12 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <div className="flex items-center border rounded-md overflow-hidden">
                <div className="bg-gray-100 px-3 py-2 border-r">
                  <Filter className="h-4 w-4 text-gray-500" />
                </div>
                <select
                  value={filterObject?.field || ""}
                  onChange={(e) =>
                    setFilterObject((prev)=>({
                      ...prev,
                      field: e.target.value,
                    }))
                  }
                  className="px-3 py-2 border-none focus:outline-none text-sm bg-transparent"
                >
                  <option value="">Select Filter Field</option>
                  {USER_FILTER_FIELD.map((filter) => (
                    <option key={filter.value} value={filter.value}>
                      {filter.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center border rounded-md overflow-hidden">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Filter Value"
                    value={filterObject?.value || ""}
                    onChange={(e) =>
                      setFilterObject((prev) => ({
                        ...prev,
                        value: e.target.value,
                      }))
                    }
                    className="pl-10 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex items-center border rounded-md overflow-hidden">
                <button
                  onClick={() => {
                    setStatusFilter((prev) => ({
                      ...prev,
                      filter: JSON.stringify(filterObject),
                    }));
                    setPageData((prev) => ({
                      ...prev,
                      currentPage: 1,
                    }));
                  }}
                  className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                >
                  <Search className="mr-2 h-4 w-4" />
                  Apply
                </button>
              </div>
              <div className="flex items-center border rounded-md overflow-hidden">
                <button
                  onClick={() => {
                    handleReset();
                    setSearchQuery("");
                  }}
                  className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                >
                  <RotateCwIcon className="mr-2 h-4 w-4" />
                  Reset
                </button>
              </div>
            </div>
          </div>

          {usersQuery.isLoading ? (
            <Skeleton.Card header rows={10} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th
                      className="px-4 py-3 text-sm font-medium text-gray-500 border-b cursor-pointer select-none"
                      onClick={() => handleSort("full_name")}
                    >
                      Name
                      {sortField === "full_name" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="px-4 py-3 text-sm font-medium text-gray-500 border-b cursor-pointer select-none"
                      onClick={() => handleSort("email")}
                    >
                      Email
                      {sortField === "email" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="px-4 py-3 text-sm font-medium text-gray-500 border-b cursor-pointer select-none"
                      onClick={() => handleSort("phone")}
                    >
                      Phone
                      {sortField === "phone" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="px-4 py-3 text-sm font-medium text-gray-500 border-b cursor-pointer select-none"
                      onClick={() => handleSort("created_at")}
                    >
                      Join Date
                      {sortField === "created_at" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    {/* <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Subscription
                    </th> */}
                    <th
                      className="px-4 py-3 text-sm font-medium text-gray-500 border-b cursor-pointer select-none"
                      onClick={() => handleSort("is_active")}
                    >
                      Status
                      {sortField === "is_active" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    {/* <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                     Is Password Reset Requested
                    </th> */}
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {usersList.length > 0 ? (
                    usersList.map((user) => (
                      <tr key={user._id} className="hover:bg-gray-50 border-b">
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                              <User className="h-4 w-4 text-gray-600" />
                            </div>
                            <div className="ml-3">
                              <div className="font-medium text-gray-900">
                                {user.full_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {user.email || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {`${user.country_code}-${user.phone}`}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {moment(user.created_at).format("DD-MM-yyyy")}
                        </td>
                        {/* <td className="px-4 py-3">
                          <span
                            className={`w-max inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              !user.subscriptionStatus
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {ADMIN_USER.includes(user.user_type)
                              ? "Admin User"
                              : user.subscriptionStatus || "Premium"}
                          </span>
                        </td> */}
                        <td className="px-4 py-3">
                          {mutation.toggleStatus.isPending ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <svg
                                className="animate-spin h-4 w-4 text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                                ></path>
                              </svg>
                              <span className="ml-1">Updating</span>
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer select-none ${
                                user.is_active
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                              onClick={() => {
                                toggleStatus(user);
                              }}
                              title="Toggle Status"
                            >
                              {user.is_active ? (
                                <CheckCircle className="mr-1 h-3 w-3" />
                              ) : (
                                <XCircle className="mr-1 h-3 w-3" />
                              )}
                              {user.is_active ? "Active" : "Inactive"}
                            </span>
                          )}
                        </td>
                        {/* <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium select-none ${
                              user.is_password_reset === false
                                ? "bg-red-100 text-red-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {user.is_password_reset === false
                              ? "No"
                              : "Yes"}
                          </span>
                        </td> */}
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <ActionButton
                            handleDelete={handleDelete}
                            item={user}
                            openModal={openModal}
                            setPermission={setPermission}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No users found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <Pagination
            currentPage={pageData.currentPage}
            onPageChange={handlePageChange}
            showing={pageData.showing}
            totalPages={pageData.totalPages}
            total={pageData.total}
          />
        </div>
      </main>

      <DeleteModal
        isOpen={deleteModal}
        onClose={handleDelete.onClose}
        onDelete={() => handleDelete.onDelete(selectedUser)}
        title="User"
      />

      {/* User Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          ></div>
          <div className="h-4/5 relative bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-xl overflow-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">
                {modalMode === "view"
                  ? "User Details"
                  : modalMode === "edit"
                  ? "Edit User"
                  : "Create User"}
              </h3>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                onClick={closeModal}
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <UserForm
                mode={modalMode}
                defaultValues={
                  {
                    ...selectedUser,
                    created_at: moment(selectedUser.created_at).format(
                      "YYYY-MM-DD"
                    ),
                  } || {}
                }
                onCancel={closeModal}
                addressList={addressList}
                onSubmit={(data) => handleSubmit(data)}
                path={path.pathname}
                submissionLoading={mutation.createUser.isPending}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
